import { useEffect, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PayPalCheckout({ 
  eventId, 
  tierName, 
  quantity = 1, 
  promoCode = null,
  totalAmount,
  onSuccess, 
  onCancel,
  onError 
}) {
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    // Fetch PayPal config
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/payments/paypal/config`);
        setClientId(res.data.client_id);
      } catch (error) {
        console.error('Failed to load PayPal config:', error);
        toast.error('Failed to load payment system');
        if (onError) onError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [onError]);

  const createOrder = async (data, actions) => {
    try {
      console.log('Creating PayPal order...');
      const res = await axios.post(`${API_URL}/payments/paypal/create-order`, {
        event_id: eventId,
        tier_name: tierName,
        quantity: quantity,
        promo_code: promoCode
      });
      
      console.log('Order created:', res.data);
      setOrderId(res.data.order_id);
      
      // Return the PayPal order ID for the SDK (Orders API v2)
      const paypalOrderId = res.data.paypal_order_id;
      console.log('Returning PayPal order ID:', paypalOrderId);
      return paypalOrderId;
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
      throw error;
    }
  };

  const onApprove = async (data, actions) => {
    console.log('PayPal onApprove called with data:', JSON.stringify(data));
    console.log('orderID from PayPal:', data.orderID);
    
    try {
      const res = await axios.post(`${API_URL}/payments/paypal/execute`, {
        orderID: data.orderID
      });
      
      console.log('Execute response:', res.data);
      toast.success('Payment successful! Tickets purchased.');
      if (onSuccess) onSuccess(res.data);
    } catch (error) {
      console.error('Payment execution failed:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', JSON.stringify(error.response?.data));
      
      const errorMessage = error.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(errorMessage);
      
      if (onError) onError(error);
    }
  };

  const handleCancel = (data) => {
    toast.info('Payment cancelled');
    if (onCancel) onCancel(data);
  };

  const handleError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment error occurred');
    if (onError) onError(err);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading payment...</span>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="text-center py-8 text-red-400">
        Payment system unavailable. Please try again later.
      </div>
    );
  }

  return (
    <PayPalScriptProvider 
      options={{ 
        clientId: clientId,
        currency: "USD",
        intent: "capture",
        components: "buttons,funding-eligibility"
      }}
    >
      <div className="paypal-button-container" data-testid="paypal-checkout" style={{ minHeight: '300px' }}>
        <PayPalButtons
          style={{
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "paypal",
            height: 45
          }}
          fundingSource={undefined}
          createOrder={createOrder}
          onApprove={onApprove}
          onCancel={handleCancel}
          onError={handleError}
          disabled={false}
        />
        <p className="text-xs text-center text-muted-foreground mt-3">
          Secure payment powered by PayPal
        </p>
      </div>
    </PayPalScriptProvider>
  );
}
